import { SegmentGroup } from "@ark-ui/react";
import classnames from "classnames";

import { useTranslation, useLocale } from "@/lib/i18n";
import panelCommonStyles from "@/features/shared/widgets/map/controls/panel-common.module.css";
import { WarningLevel, warningLevelColorMap } from "@/lib/warning-level";
import {
  isMaritimeWarningDomainId,
  type MaritimeWarningDomainId,
} from "@/lib/maritime-domains";

import { formatWarningLevelBadge } from "./maritime-warnings.utils";
import badgeStyles from "./warning-level-badge.module.css";
import styles from "./maritime-area-category-control.module.css";

export type MaritimeAreaCategoryControlProps = {
  worstLevelByDomainId: Record<MaritimeWarningDomainId, WarningLevel>;
  enabledDomainIds: MaritimeWarningDomainId[];
  onValueChange: (domainId: MaritimeWarningDomainId) => void;
  value: MaritimeWarningDomainId;
};

type CategorySegmentProps = {
  domainId: MaritimeWarningDomainId;
  labelKey:
    | "dashboard.warnings.interactiveMap.categories.coastal"
    | "dashboard.warnings.interactiveMap.categories.north-sea";
  level: WarningLevel;
};

const CategorySegment = ({ domainId, labelKey, level }: CategorySegmentProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const badgeColor = level > WarningLevel.Green ? warningLevelColorMap[level] : undefined;

  return (
    <SegmentGroup.Item
      className={classnames(styles.item, panelCommonStyles.rowDivider)}
      value={domainId}
    >
      <SegmentGroup.ItemText className={styles.itemText}>
        {t(labelKey)}
      </SegmentGroup.ItemText>
      {badgeColor && (
        <span className={badgeStyles.block}>
          <span className={badgeStyles.label}>
            {formatWarningLevelBadge(level, locale)}
          </span>
          <span
            className={badgeStyles.dot}
            style={{ backgroundColor: badgeColor }}
            aria-hidden="true"
          />
        </span>
      )}
      <SegmentGroup.ItemHiddenInput />
    </SegmentGroup.Item>
  );
};

const MaritimeAreaCategoryControl = (props: MaritimeAreaCategoryControlProps) => {
  const { t } = useTranslation();
  const { enabledDomainIds, onValueChange, value, worstLevelByDomainId } = props;

  const handleValueChange = (details: { value: string | null }) => {
    const next = details.value;
    if (next && isMaritimeWarningDomainId(next)) {
      onValueChange(next);
    }
  };

  return (
    <SegmentGroup.Root
      aria-label={t("dashboard.warnings.interactiveMap.categoryToggleAria")}
      className={classnames(
        styles.root,
        panelCommonStyles.frostedPanel,
        panelCommonStyles.mapPanel,
      )}
      onValueChange={handleValueChange}
      orientation="vertical"
      value={value}
    >
      {enabledDomainIds.includes("coastal") && (
        <CategorySegment
          domainId="coastal"
          labelKey="dashboard.warnings.interactiveMap.categories.coastal"
          level={worstLevelByDomainId.coastal}
        />
      )}
      {enabledDomainIds.includes("north-sea") && (
        <CategorySegment
          domainId="north-sea"
          labelKey="dashboard.warnings.interactiveMap.categories.north-sea"
          level={worstLevelByDomainId["north-sea"]}
        />
      )}
    </SegmentGroup.Root>
  );
};

export default MaritimeAreaCategoryControl;
